import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { ClaimsInquiryDetails } from 'components/ClaimsInquiryDetails';
import AppDataContext from 'contexts/appData.context';
import { useDebounce } from 'hooks/useDebounce';
import useFetchSelectMenuItems from 'hooks/useFetchSelectMenuItems';

import { AOGroupedSelect } from 'components/AOGroupedSelect';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Col,
  DataGrid,
  DatePicker,
  Paper,
  Row,
  Select,
  TextField,
  ViewContainer,
} from 'components/AORedesign';
import { AOTransferList } from 'components/AOTransferList';
import {
  fetchAgencyNameSuggestions,
  handleAOAutocompleteOnBlur,
  handleAOAutocompleteOnChange,
  handleAOAutocompleteOnInputChange,
  handleFromDateOnChange,
  handleOnChange,
  handleToDateOnChange,
} from 'components/CommonUtilityServices/EventHandlerService';
import { Feature } from 'components/Feature';
import { GET_PERM_AGENCIES } from 'constants/api';
import { ROUTE_REDESIGN_REPORTS } from 'constants/routes';
import { aODatePickerMinDate } from 'helpers';
import {
  fetchFormSuggestions,
  fetchTAReportsFormData,
  getReportResultsColumns,
  handleAOTransferListSetLeft,
  handleAOTransferListSetRight,
  handleClickPaginationChange,
  handleCreateTransactionActivityReport,
  handleExportDropdownOnChange,
  handleGenerateOnClick,
  handleLOBOnChange,
  handleOnRowDoubleClick,
  handleOnSortModelChange,
  periodMenuItems,
} from './TransactionActivityReports.api';

const TransactionActivityReports = () => {
  const [lOBs, setLOBs] = useState([{ lobName: 'ALL', lobGroupName: 'ALL' }]);
  const [transactionTypes, setTransactionTypes] = useState([{ value: 'ALL', label: 'ALL' }]);
  const [transactionSubtypes, setTransactionSubtypes] = useState([]);
  const [checkedTransferListItems, setCheckedTransferListItems] = useState([]);
  const [autocompleteOptions, setAutocompleteOptions] = useState({
    policyNumber: [],
    agencyCodes: [],
    accountNumber: [],
    partialInsuredName: [],
  });
  const [permAgenciesOptions, setPermAgenciesOptions] = useState([]);
  const [reportResults, setReportResults] = useState({});
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [downloadingPdfError, setDownloadingPdfError] = useState(false);
  const [exportRangeMenuItems, setExportRangeMenuItems] = useState([]);
  const [exportRangeValue, setExportRangeValue] = useState(1);
  const [createButtonLoading, setCreateButtonLoading] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(false);
  const [reportError, setReportError] = useState('');
  const [autocompleteInputValues, setAutocompleteInputValues] = useState({
    policyNumber: { value: '', invalidText: '' },
    agencyCodes: { value: '' },
    accountNumber: { value: '', invalidText: '' },
    partialInsuredName: { value: '', invalidText: '' },
  });
  const [formData, setFormData] = useState({
    accountNumber: '',
    agencyCodes: '',
    pageNumber: 1,
    fromDate: null,
    lob: 'ALL',
    lobGroup: 'ALL',
    partialInsuredName: '',
    period: periodMenuItems[0].value,
    policyNumber: '',
    toDate: null,
    transactionSubTypes: [],
    transactionType: 'ALL',
  });

  const [debouncedState, setDebouncedState] = useDebounce(autocompleteInputValues);

  const generateReportFormDataRef = useRef({});

  // Mirrors the latest `formData` so callbacks that outlive their render can read it. Distinct from
  // `generateReportFormDataRef` above, which deliberately LAGS the form — that one snapshots the
  // criteria that produced the rendered grid so Generate's PDF matches what is on screen. This one
  // must lead.
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const history = useHistory();
  const { setPreviousPageName } = useContext(AppDataContext);

  // `useFetchSelectMenuItems` runs its request in a `useEffect(..., [])`, so this callback is
  // created once on mount and never replaced — the `formData` in its scope is frozen at the first
  // render. The response lands whenever the network delivers it, which can be seconds later and
  // well after the user has started filling the form, so reading that stale binding would both
  // discard their edits (writing back the mount-time snapshot — a period already switched to
  // Custom Dates would revert, closing the date pickers) and submit the default `agencyCodes: ''`.
  // Read through `formDataRef` instead, and derive the one object used for both the state update
  // and the submit, so neither depends on React having committed yet.
  useFetchSelectMenuItems(GET_PERM_AGENCIES, (response) => {
    // External users get data from this call. Internal returns an empty array — no default agency,
    // and no auto-generated grid.
    if (!response.data.length) return;

    const allAgencies = { label: `ALL - 000000`, value: '000000' };
    setPermAgenciesOptions([
      allAgencies,
      ...response.data.map((agency) => ({
        label: `${agency.agencyName?.trim()} - ${agency.agencyCode}`,
        value: agency.agencyCode,
      })),
    ]);

    const nextFormData = { ...formDataRef.current, agencyCodes: allAgencies.value };
    setFormData(nextFormData);

    // If external user, generate the Grid
    handleCreateTransactionActivityReport(
      nextFormData,
      setReportResults,
      generateReportFormDataRef,
      setExportRangeMenuItems,
      setExportRangeValue,
      setCreateButtonLoading,
      setReportError,
    );
  });

  useEffect(() => {
    (async () => {
      const tAReportsTransactionSubtypes = await fetchTAReportsFormData('transaction-subtypes');
      setTransactionSubtypes(tAReportsTransactionSubtypes.filter((item) => item !== 'Farm and Ranch'));
      const tAReportsLOBs = await fetchTAReportsFormData('LOBs');
      setLOBs(tAReportsLOBs.filter((lob) => lob.lobName !== 'Farm and Ranch'));
      const tAReportsTransactionTypes = await fetchTAReportsFormData('transaction-types');
      const transactionTypeMenuItems = tAReportsTransactionTypes.map((item) => ({
        value: item,
        label: item,
      }));
      setTransactionTypes(transactionTypeMenuItems);
    })();
  }, []);

  const updateAutocompleteOptions = async (field, fetcher) => {
    const value = autocompleteInputValues[field].value;
    const suggestions = value.length >= 3 ? await fetcher(value) : [];
    setAutocompleteOptions((prev) => ({ ...prev, [field]: suggestions }));
  };

  useEffect(() => {
    updateAutocompleteOptions('policyNumber', (v) => fetchFormSuggestions('policy-suggestions', v));
  }, [debouncedState.policyNumber]);

  useEffect(() => {
    updateAutocompleteOptions('agencyCodes', fetchAgencyNameSuggestions);
  }, [debouncedState.agencyCodes]);

  useEffect(() => {
    updateAutocompleteOptions('accountNumber', (v) => fetchFormSuggestions('account-suggestions', v));
  }, [debouncedState.accountNumber]);

  useEffect(() => {
    updateAutocompleteOptions('partialInsuredName', (v) => fetchFormSuggestions('account-suggestions', v));
  }, [debouncedState.partialInsuredName]);

  const handleOnChange_PROXY = (e) => handleOnChange(e, formData, setFormData);

  const handleLOBOnChange_PROXY = (e) => handleLOBOnChange(e, lOBs, formData, setFormData);

  const handleFromDateOnChange_PROXY = (date) => handleFromDateOnChange(date, formData, setFormData);

  const handleToDateOnChange_PROXY = (date) => handleToDateOnChange(date, formData, setFormData);

  const handleAOTransferListSetLeft_PROXY = (data) => handleAOTransferListSetLeft(data, setTransactionSubtypes);

  const handleAOTransferListSetRight_PROXY = (data) => handleAOTransferListSetRight(data, formData, setFormData);

  const handleCreateTransactionActivityReport_PROXY = () => {
    setReportError('');
    handleCreateTransactionActivityReport(
      formData,
      setReportResults,
      generateReportFormDataRef,
      setExportRangeMenuItems,
      setExportRangeValue,
      setCreateButtonLoading,
      setReportError,
    );
  };

  const handlePolicyNumberOnInputChange_PROXY = (e, value) =>
    handleAOAutocompleteOnInputChange('policyNumber', e, value, autocompleteInputValues, setAutocompleteInputValues, debouncedState, setDebouncedState);

  const handlePolicyNumberOnChange = (e, value) => handleAOAutocompleteOnChange('policyNumber', e, value, formData, setFormData);

  const handlePolicyNumberOnBlur = () => handleAOAutocompleteOnBlur('policyNumber', formData, setFormData, autocompleteInputValues);

  const handleAgencyNameOnInputChange = (e, value) =>
    handleAOAutocompleteOnInputChange('agencyCodes', e, value, autocompleteInputValues, setAutocompleteInputValues, debouncedState, setDebouncedState);

  const handleAgencyNameOnChange = (e, value) => handleAOAutocompleteOnChange('agencyCodes', e, value, formData, setFormData);

  const handleAgencyNameOnBlur = () => handleAOAutocompleteOnBlur('agencyCodes', formData, setFormData, autocompleteInputValues);

  const handleAccountNumberOnInputChange = (e, value) =>
    handleAOAutocompleteOnInputChange('accountNumber', e, value, autocompleteInputValues, setAutocompleteInputValues, debouncedState, setDebouncedState);

  const handleAccountNumberOnChange = (e, value) => handleAOAutocompleteOnChange('accountNumber', e, value, formData, setFormData);

  const handleAccountNumberOnBlur = () => handleAOAutocompleteOnBlur('accountNumber', formData, setFormData, autocompleteInputValues);

  const handlePartialInsuredNameOnInputChange = (e, value) =>
    handleAOAutocompleteOnInputChange('partialInsuredName', e, value, autocompleteInputValues, setAutocompleteInputValues, debouncedState, setDebouncedState);

  const handlePartialInsuredNameOnChange = (e, value) => handleAOAutocompleteOnChange('partialInsuredName', e, value, formData, setFormData);

  const handlePartialInsuredNameOnBlur = () => handleAOAutocompleteOnBlur('partialInsuredName', formData, setFormData, autocompleteInputValues);

  const handleGenerateOnClick_PROXY = () => handleGenerateOnClick(generateReportFormDataRef, setLoadingPdf, setDownloadingPdfError, exportRangeValue);

  const handleOnSortModelChange_PROXY = (sortData) => handleOnSortModelChange(sortData, generateReportFormDataRef);

  const datePickerMinDate = aODatePickerMinDate(undefined, undefined, 1);

  const handleClickPaginationChange_PROXY = (page) => handleClickPaginationChange(page, formData, setReportResults, setCreateButtonLoading);

  const handleExportDropdownOnChange_PROXY = (e) => handleExportDropdownOnChange(e, setExportRangeValue);

  const handleOnRowDoubleClick_PROXY = (params) => handleOnRowDoubleClick(params, history, setPreviousPageName, setSelectedClaim, setReportError);

  const handleCloseClaimsInquiryDetails = () => setSelectedClaim(false);

  return (
    <ViewContainer returnRoute={{ route: ROUTE_REDESIGN_REPORTS, text: 'Reports' }}>
      <Card>
        <CardHeader title="Transaction Activity Report" />
        <CardContent>
          <Box mb={2}>
            <Alert
              severity="info"
              mb={2}
              message="Due to system batch processing, transaction dates on the report may appear to be later than dates entered into the system."
            />
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleGenerateOnClick_PROXY();
              }}>
              <Box mb={3}>
                <Row spacing={2} alignItems="center">
                  <Col>
                    <Select
                      onChange={handleOnChange_PROXY}
                      value={formData.period}
                      menuItems={periodMenuItems}
                      id="transaction-activity-report-period"
                      labelId="transaction-activity-report-period-label"
                      label="Period"
                      name="period"
                    />
                  </Col>
                  {formData?.period === -1 && (
                    <>
                      <Col mb={3}>
                        <DatePicker
                          id="transaction-activity-report-from"
                          onChange={handleFromDateOnChange_PROXY}
                          minDate={datePickerMinDate}
                          value={formData.fromDate}
                          label="From"
                          name="fromDate"
                          minDateMessage="Results only return activity for past 12 months"
                          disableFuture={true}
                        />
                      </Col>
                      <Col mb={3}>
                        <DatePicker
                          id="transaction-activity-report-to"
                          onChange={handleToDateOnChange_PROXY}
                          minDate={datePickerMinDate}
                          value={formData.toDate}
                          label="To"
                          name="toDate"
                          minDateMessage="Results only return activity for past 12 months"
                          disableFuture={true}
                        />
                      </Col>
                    </>
                  )}
                </Row>
              </Box>

              <Box mb={3}>
                <Feature allowed="isAgent">
                  <Box mb={3}>
                    <Select
                      onChange={handleOnChange_PROXY}
                      value={formData.agencyCodes}
                      menuItems={permAgenciesOptions}
                      id="transaction-activity-report-agency-name"
                      labelId="transaction-activity-report-agency-name-label"
                      label="Agency Name"
                      name="agencyCodes"
                    />
                  </Box>
                </Feature>
                <Box mb={3}>
                  <Autocomplete
                    style={{ width: 400 }}
                    id="transaction-activity-report-policy-number"
                    value={formData.policyNumber}
                    inputValue={autocompleteInputValues.policyNumber.value}
                    options={autocompleteOptions.policyNumber || []}
                    disableClearable
                    onChange={handlePolicyNumberOnChange}
                    onInputChange={handlePolicyNumberOnInputChange_PROXY}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={autocompleteInputValues.policyNumber.invalidText.length > 0}
                        helperText={autocompleteInputValues.policyNumber.invalidText}
                        label="Policy Number"
                        placeholder="Enter Policy Number"
                        variant="outlined"
                        onBlur={handlePolicyNumberOnBlur}
                      />
                    )}
                  />
                </Box>
                <Feature allowed="internalUser">
                  <Autocomplete
                    style={{ width: 400 }}
                    id="transaction-activity-report-agency-name"
                    value={formData.agencyCodes}
                    inputValue={autocompleteInputValues.agencyCodes.value}
                    options={autocompleteOptions.agencyCodes || []}
                    disableClearable
                    required={true}
                    onChange={handleAgencyNameOnChange}
                    onInputChange={handleAgencyNameOnInputChange}
                    renderInput={(params) => (
                      <TextField {...params} label="Agency Name" placeholder="Enter Agency Name" variant="outlined" onBlur={handleAgencyNameOnBlur} />
                    )}
                  />
                </Feature>
              </Box>
              <Box mb={3}>
                <Autocomplete
                  style={{ width: 400 }}
                  id="transaction-activity-report-account-number"
                  value={formData.accountNumber}
                  inputValue={autocompleteInputValues.accountNumber.value}
                  options={autocompleteOptions.accountNumber || []}
                  disableClearable
                  onChange={handleAccountNumberOnChange}
                  onInputChange={handleAccountNumberOnInputChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={autocompleteInputValues.accountNumber.invalidText.length > 0}
                      helperText={autocompleteInputValues.accountNumber.invalidText}
                      label="Account Number"
                      placeholder="Enter Account Number"
                      variant="outlined"
                      onBlur={handleAccountNumberOnBlur}
                    />
                  )}
                />
              </Box>
              <Box mb={3}>
                <Autocomplete
                  style={{ width: 400 }}
                  id="transaction-activity-report-name-insured"
                  value={formData.partialInsuredName}
                  inputValue={autocompleteInputValues.partialInsuredName.value}
                  options={autocompleteOptions.partialInsuredName || []}
                  disableClearable
                  onChange={handlePartialInsuredNameOnChange}
                  onInputChange={handlePartialInsuredNameOnInputChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={autocompleteInputValues.partialInsuredName.invalidText.length > 0}
                      helperText={autocompleteInputValues.partialInsuredName.invalidText}
                      label="Name Insured"
                      placeholder="Enter Name Insured"
                      variant="outlined"
                      onBlur={handlePartialInsuredNameOnBlur}
                    />
                  )}
                />
              </Box>
              <Box mb={3}>
                <Row spacing={2} alignItems="center">
                  <Col>
                    <AOGroupedSelect
                      onChange={handleLOBOnChange_PROXY}
                      value={formData.lob}
                      menuItems={lOBs.map((lob) => ({ group: lob.lobGroupName, value: lob.lobName, code: lob.lobName }))}
                      id="transaction-activity-report-lob"
                      labelId="transaction-activity-report-lob-label"
                      inputLabel="Line of Business"
                      name="lob"
                    />
                  </Col>
                  <Col>
                    <Select
                      onChange={handleOnChange_PROXY}
                      value={formData.transactionType}
                      menuItems={transactionTypes}
                      id="transaction-activity-report-transaction-type"
                      labelId="transaction-activity-report-transaction-type-label"
                      label="Transaction Type"
                      name="transactionType"
                    />
                  </Col>
                </Row>
              </Box>
              <Box mb={3}>
                <AOTransferList
                  setLeft={handleAOTransferListSetLeft_PROXY}
                  setRight={handleAOTransferListSetRight_PROXY}
                  setChecked={setCheckedTransferListItems}
                  left={transactionSubtypes}
                  right={formData.transactionSubTypes}
                  checked={checkedTransferListItems}
                  leftLabel="Available Transaction Subtypes"
                  rightLabel="Selected Transaction Subtypes"
                />
              </Box>
              <Box mb={3}>
                <Button
                  id="transaction-activity-report-create-button"
                  onClick={handleCreateTransactionActivityReport_PROXY}
                  loading={createButtonLoading}
                  variant="contained"
                  color="primary">
                  Create
                </Button>
              </Box>
              {reportError && <Alert message={reportError} severity="error" />}
              {downloadingPdfError && <Alert message={downloadingPdfError} severity="error" />}
              {reportResults.total > 0 && (
                <Box mb={3}>
                  <Row spacing={2} alignItems="center">
                    <Col>
                      <Select
                        onChange={handleExportDropdownOnChange_PROXY}
                        value={exportRangeValue}
                        menuItems={exportRangeMenuItems}
                        id="transaction-activity-export-select"
                        labelId="transaction-activity-export-select-label"
                        label="Export Range"
                        name="export-range"
                      />
                    </Col>
                    <Col>
                      <Button onClick={handleGenerateOnClick_PROXY} loading={loadingPdf} variant="contained" color="primary">
                        Generate
                      </Button>
                    </Col>
                  </Row>
                </Box>
              )}
            </form>
          </Box>

          <Paper>
            <DataGrid
              onSortModelChange={handleOnSortModelChange_PROXY}
              onPageChange={handleClickPaginationChange_PROXY}
              onRowDoubleClick={handleOnRowDoubleClick_PROXY}
              rows={reportResults.data?.map((el) => ({
                id: el.SeqId,
                ...el,
              }))}
              columns={getReportResultsColumns(setSelectedClaim, history, setPreviousPageName, setReportError)}
              rowCount={reportResults.total}
              loading={createButtonLoading}
              pageSize={25}
              rowsPerPageOptions={[25]}
              paginationMode="server"
            />
          </Paper>

          <ClaimsInquiryDetails handleCloseClaimsInquiryDetails={handleCloseClaimsInquiryDetails} selectedClaim={selectedClaim} />
        </CardContent>
      </Card>
    </ViewContainer>
  );
};

export default TransactionActivityReports;
